import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { CategoryResultsFilter, ALL_CATEGORIES } from '../components/competition/CategoryResultsFilter';
import { ResultsPrintSheet } from '../components/competition/ResultsPrintSheet';
import { SPONSORS_PRINT_CSS } from '../components/competition/results-print-sponsors';
import { useCompetitionResults } from '../components/competition/useCompetitionResults';
import { useCategoryParam, withCategory } from '../components/competition/useCategoryParam';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { competitionsService } from '../services/competitions';
import { printNode } from '../utils/print-node';
import type { Competition } from '../types/competition';

/**
 * `/competitions/:slug/results/print` — a stage's results as a sheet to print
 * or save as a PDF: every class, or the one picked, optionally one class to a
 * page. Public, like the results themselves. The pick is the URL's
 * (`?category=`), as on the stage's page, which links here with its own.
 *
 * What is on screen is a preview; the printer gets the sheet alone
 * (`printNode`), so no navbar, no controls and no dark theme reach the paper —
 * Ctrl/Cmd+P here means the sheet too. The button waits until every class has
 * answered, so nothing is printed as a skeleton.
 */
export default function CompetitionResultsPrintPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [competition, setCompetition] = useState<Competition | null>(null);
  const [missing, setMissing] = useState(false);
  const [classPerPage, setClassPerPage] = useState(false);
  const { categories, resultsOf, listed, settled } = useCompetitionResults(slug);
  const [filter, setFilter] = useCategoryParam(categories);
  const sheetRef = useRef<HTMLDivElement>(null);

  // Back to the stage's page on the class looked at here.
  const profilePath = withCategory(`/competitions/${slug}`, filter);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    competitionsService
      .getCompetitionBySlug(slug)
      .then((found) => active && setCompetition(found))
      .catch(() => active && setMissing(true));
    return () => {
      active = false;
    };
  }, [slug]);

  const shown = filter === ALL_CATEGORIES ? categories : categories.filter(({ id }) => id === filter);
  const picked = filter === ALL_CATEGORIES ? null : shown[0];
  const ready = !!competition && settled;

  const handlePrint = useCallback(() => {
    const node = sheetRef.current;
    if (!node || !ready) return;
    printNode(node, {
      // What "Save as PDF" names the file.
      title: [t('competitionStandings.results'), competition?.name, picked?.name].filter(Boolean).join(' - '),
      pageMargin: '14mm',
      // The sponsors at the foot of every page.
      css: SPONSORS_PRINT_CSS,
    });
  }, [competition?.name, picked?.name, ready, t]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        handlePrint();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePrint]);

  useBreadcrumb(
    () => ({
      onBack: () => navigate(profilePath),
      segments: [
        ...(competition ? [{ label: competition.name, onClick: () => navigate(profilePath) }] : []),
        { label: t('competitionStandings.printTitle'), muted: true },
      ],
      loading: !competition && !missing,
    }),
    [competition?.name, profilePath, missing]
  );

  if (!slug) return <Navigate to="/not-found" replace />;

  if (missing || (listed && categories.length === 0)) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <CardContent className="space-y-4 pt-6 text-center" data-testid="results-print-unavailable">
            <p className="text-muted-foreground">{t('competitionStandings.printUnavailable')}</p>
            <Button variant="outline" onClick={() => navigate(profilePath)} className="gap-2">
              <ArrowLeft className="size-4" />
              {t('competitionStandings.backToCompetition')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="competition-results-print">
      <div className="space-y-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold">{t('competitionStandings.printTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('competitionStandings.printDescription')}</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {categories.length > 1 && (
            <CategoryResultsFilter categories={categories} value={filter} onChange={setFilter} />
          )}
          {shown.length > 1 && (
            <div className="flex items-center gap-2">
              <Switch id="class-per-page" checked={classPerPage} onCheckedChange={setClassPerPage} />
              <Label htmlFor="class-per-page">{t('competitionStandings.classPerPage')}</Label>
            </div>
          )}
        </div>

        <Button onClick={handlePrint} disabled={!ready} className="gap-2" data-testid="results-print-button">
          <Printer className="size-4" />
          {t('competitionStandings.print')}
        </Button>
      </div>

      {/* The preview: the sheet on a white page with the paper's margins.
          Only the sheet inside is what gets printed. */}
      <div className="rounded-xl border bg-muted/30 p-2 sm:p-5">
        <div className="mx-auto max-w-[210mm] bg-white p-4 shadow-lg sm:p-[14mm]">
          {ready ? (
            <ResultsPrintSheet
              ref={sheetRef}
              competition={competition}
              categories={shown}
              resultsOf={resultsOf}
              classPerPage={classPerPage}
            />
          ) : (
            <div className="space-y-3" data-testid="results-print-loading">
              <Skeleton className="h-16 w-2/3 bg-gray-200" />
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-8 w-full bg-gray-200" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
